/**
 * Pure component for the New User app, which is used to provide new users of
 * Sparx Web with their passwords for the first time.
 */

import React from 'react';
import PropTypes from 'prop-types';
import IPropTypes from 'react-immutable-proptypes';
import { CSSTransitionGroup } from 'react-transition-group';

import {
    AlreadySetUpView,
    ChoosePasswordView,
    ConfirmDetailsView,
    InfoBoxView,
    NewUserView,
    PasswordRevealView,
    UsernameRevealView,
    PasswordResetRequestedView,
} from 'phoenix2/components/views/new-user-view';
import Alert from 'phoenix2/components/dialogs';
import { ImplicitWait } from 'phoenix2/components/widgets/wait';
import {
    ALREADY_SET_UP,
    CHOOSE_PASSWORD,
    CONFIRM_DETAILS,
    INFO_BOX,
    USERNAME_REVEAL,
    PASSWORD_RESET_REQUESTED,
} from 'phoenix2/state/viewstates/view-ids';
import spxLogo from 'imported-assets/img/products/sparx_maths_blue_logo.svg';
import background from 'imported-assets/img/restyle_background_gradient.svg';

export class NewUser extends React.Component {
    static displayName = 'NewUser';

    componentDidMount() {
        // Set keyboard focus to be ready to tab into the main view
        this._keyboardFocusStartPoint.focus();
    }

    componentDidUpdate(prevProps) {
        // If we have just changed to a new view, set the keyboard focus to be
        // ready to tab into the new view
        if (prevProps.viewType !== this.props.viewType) {
            this._keyboardFocusStartPoint.focus();
        }
    }

    render() {
        const { transition, viewType, alert, disableAnimations, usingChoosePassword } = this.props;

        const params = new URLSearchParams(window?.location.search);
        const ssoProvider = params.get('sso_provider');
        const ssoURL = params.get('sso_url');

        let viewComponent = null;
        let location = null;

        switch (viewType) {
            case 'new-user':
                viewComponent = (
                    <NewUserView key="new-user" ssoProvider={ssoProvider} ssoURL={ssoURL} />
                );
                location = usingChoosePassword
                    ? settings.nuForgotDetailsMode
                        ? 'Forgot login details'
                        : 'Create your account'
                    : 'Find out your Sparx username and password';
                break;
            case 'password-reveal':
                viewComponent = <PasswordRevealView key="password-reveal" />;
                location = 'Remember your username and password';
                break;
            case USERNAME_REVEAL:
                viewComponent = <UsernameRevealView key={USERNAME_REVEAL} />;
                location = 'Step 1. Remember your username';
                break;
            case CHOOSE_PASSWORD:
                viewComponent = <ChoosePasswordView key={CHOOSE_PASSWORD} />;
                location = 'Step 2. Choose your password';
                break;
            case CONFIRM_DETAILS:
                viewComponent = <ConfirmDetailsView key={CONFIRM_DETAILS} />;
                location = 'Step 3. Confirm your details';
                break;
            case INFO_BOX:
                viewComponent = <InfoBoxView key={INFO_BOX} />;
                location = '';
                break;
            case PASSWORD_RESET_REQUESTED:
                viewComponent = <PasswordResetRequestedView key={PASSWORD_RESET_REQUESTED} />;
                location = '';
                break;
            case ALREADY_SET_UP:
                viewComponent = <AlreadySetUpView key={ALREADY_SET_UP} />;
                location = '';
                break;
            default:
                viewComponent = <p key="default" className="view" />;
        }

        const transitionName = transition === 'pop' ? 'rev-view' : 'view';

        let transitionGroup = null;

        if (!disableAnimations) {
            transitionGroup = (
                <CSSTransitionGroup
                    component="div"
                    transitionName={transitionName}
                    transitionEnterTimeout={500}
                    transitionLeaveTimeout={500}
                >
                    <div className="view new-user-view">
                        <div className="new-user-view-container">
                            <div className="new-user-brand-container">
                                <div className="new-user-brand-content">
                                    <img src={spxLogo} alt="Sparx Maths" />
                                    <span>{location}</span>
                                </div>
                            </div>
                            {viewComponent}
                        </div>
                    </div>
                </CSSTransitionGroup>
            );
        } else {
            transitionGroup = <div>{viewComponent}</div>;
        }

        return (
            <div className="new-user-screen">
                <img src={background} className="new-user-background" />
                <>
                    <ImplicitWait />
                    <Alert
                        alertSpec={alert}
                        onClosed={() => this._keyboardFocusStartPoint.focus()}
                    />
                    <div
                        className="keyboard-focus-start-point"
                        tabIndex="-1"
                        ref={el => (this._keyboardFocusStartPoint = el)}
                    />
                    <div className="main-view">{transitionGroup}</div>
                </>
            </div>
        );
    }
}

/**
 * Props for the New User App:
 * - transition - used to determine the view transition animation.
 * - viewType - the type of the current view (e.g. 'mainmenu')
 * - alert - alert object, if provided an alert is shown
 * - disableAnimations - boolean whether to disable animations
 */
NewUser.propTypes = {
    transition: PropTypes.string,
    viewType: PropTypes.string,
    alert: IPropTypes.map,
    disableAnimations: PropTypes.bool,
    usingChoosePassword: PropTypes.bool,
};

export default NewUser;
